import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { VoiceProfileSchema, type VoiceProfile } from "./voice-style";

const SamplesInput = z.object({
  samples: z
    .array(z.string().min(80, "Each sample needs at least ~80 characters.").max(8000))
    .min(1, "Add at least one writing sample.")
    .max(3, "Up to 3 samples."),
});

async function analyzeSamples(samples: string[]): Promise<VoiceProfile> {
  const { generateText } = await import("ai");
  const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
  const key = process.env.LOVABLE_API_KEY;
  if (!key) throw new Error("Missing LOVABLE_API_KEY");
  const gateway = createLovableAiGatewayProvider(key);
  const model = gateway("google/gemini-2.5-flash");

  const samplesBlock = samples
    .map((s, i) => `SAMPLE ${i + 1}:\n"""\n${s.slice(0, 8000)}\n"""`)
    .join("\n\n");

  const system = `You are a stylometry analyst. Given 1-3 short writing samples from ONE student, produce a structured style profile so another writer (or AI) can mimic their voice.

Be specific and concrete — never vague. Pull actual phrases the student uses.

Return ONLY raw JSON in this exact shape (no markdown, no commentary):
{
  "avgSentenceLength": <number, average words per sentence across all samples, integer>,
  "sentenceLengthVariance": "low" | "medium" | "high",
  "vocabularyTier": "basic" | "intermediate" | "elevated" | "academic",
  "usesContractions": <boolean — does the student use it's/don't/you're naturally?>,
  "favoriteTransitions": [<up to 8 transition phrases the student actually uses, verbatim>],
  "tone": "<1-2 sentences describing the student's tone (e.g. 'wry and self-aware, slightly informal, leans on rhetorical questions')>",
  "quirks": [<up to 6 specific stylistic quirks: em-dash use, semicolons, parentheticals, sentence fragments, starting sentences with conjunctions, etc.>],
  "openingPatterns": [<up to 5 ways the student tends to open sentences or paragraphs, with examples>],
  "tabooWords": [<up to 10 words the student conspicuously avoids, OR words that would clearly clash with their voice>],
  "snippetExcerpts": [<1-2 short (under 400 chars) excerpts from the samples that best capture the student's voice — pick the most representative passages, verbatim>],
  "summary": "<one paragraph (3-5 sentences) describing the student's writing voice in plain language so a teacher or AI could mimic it>"
}`;

  const { text } = await generateText({
    model,
    system,
    prompt: `Analyze the following ${samples.length} writing sample(s) and produce the JSON profile.\n\n${samplesBlock}`,
    temperature: 0.4,
  });

  const cleaned = text
    .replace(/^```json\s*/im, "")
    .replace(/^```\s*/im, "")
    .replace(/```\s*$/im, "")
    .trim();
  const s = cleaned.indexOf("{");
  const e = cleaned.lastIndexOf("}");
  const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonStr) as Record<string, unknown>;
  } catch {
    throw new Error("Could not parse voice profile. Try again with slightly longer samples.");
  }
  // Defensively clamp array fields the model sometimes over-fills.
  const clamp = (key: string, max: number) => {
    const v = parsed[key];
    if (Array.isArray(v) && v.length > max) parsed[key] = v.slice(0, max);
  };
  const clampStringArray = (key: string, maxLen: number) => {
    const v = parsed[key];
    if (Array.isArray(v)) {
      parsed[key] = (v as string[]).map((s) =>
        typeof s === "string" && s.length > maxLen ? s.slice(0, maxLen) : s,
      );
    }
  };
  clamp("snippetExcerpts", 2);
  clamp("favoriteTransitions", 12);
  clamp("quirks", 10);
  clamp("openingPatterns", 8);
  clamp("tabooWords", 20);
  clampStringArray("favoriteTransitions", 80);
  clampStringArray("quirks", 160);
  clampStringArray("openingPatterns", 160);
  clampStringArray("tabooWords", 80);
  clampStringArray("snippetExcerpts", 600);
  return VoiceProfileSchema.parse(parsed);
}

export const analyzeAndSaveVoiceProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SamplesInput.parse(input))
  .handler(async ({ data, context }) => {
    const profile = await analyzeSamples(data.samples);
    const { error } = await context.supabase
      .from("voice_profiles")
      .upsert(
        {
          user_id: context.userId,
          samples: data.samples as never,
          profile: profile as never,
          enabled: true,
        },
        { onConflict: "user_id" },
      );
    if (error) throw new Error(error.message);
    return { profile };
  });

export const analyzeSamplesAdHoc = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SamplesInput.parse(input))
  .handler(async ({ data }) => {
    const profile = await analyzeSamples(data.samples);
    return { profile };
  });

export const getVoiceProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("voice_profiles")
      .select("samples, profile, enabled, updated_at")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return { exists: false as const };
    return {
      exists: true as const,
      samples: (data.samples ?? []) as string[],
      profile: data.profile as VoiceProfile | null,
      enabled: data.enabled as boolean,
      updatedAt: data.updated_at as string,
    };
  });

export const setVoiceEnabled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ enabled: z.boolean() }).parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("voice_profiles")
      .update({ enabled: data.enabled })
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { enabled: data.enabled };
  });

export const deleteVoiceProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { error } = await context.supabase
      .from("voice_profiles")
      .delete()
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Pull writing samples from past tutor sessions ----------

/**
 * Returns recent tutor sessions for the user with the student's own writing
 * extracted (drafts for `tutor_work` entries; user messages for graded `tutor`
 * entries). Filters out sessions with too little student text to be useful as
 * a voice sample.
 */
export const listTutorVoiceSamples = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("generations")
      .select("id, title, created_at, output")
      .eq("user_id", context.userId)
      .eq("assignment_type", "tutor")
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error(error.message);

    type Row = { id: string; title: string; created_at: string; sample: string };
    const out: Row[] = [];
    for (const row of data ?? []) {
      const o = (row.output ?? {}) as Record<string, unknown>;
      let text = "";
      if (o.kind === "tutor_work" && Array.isArray(o.drafts)) {
        text = (o.drafts as unknown[])
          .filter((d): d is string => typeof d === "string")
          .map((d) => d.trim())
          .filter(Boolean)
          .join("\n\n");
      } else if (o.kind === "tutor" && Array.isArray(o.messages)) {
        text = (o.messages as Array<{ role?: string; content?: string }>)
          .filter((m) => m?.role === "user" && typeof m.content === "string")
          .map((m) => (m.content as string).trim())
          .filter(Boolean)
          .join("\n\n");
      }
      const trimmed = text.trim();
      if (trimmed.length >= 80) {
        out.push({
          id: row.id as string,
          title: (row.title as string) || "Tutor session",
          created_at: row.created_at as string,
          sample: trimmed.slice(0, 8000),
        });
      }
    }
    return out;
  });

